// utils/errorHandler.js

/**
 * Comprehensive error handling utilities
 */
export class ErrorHandler {
  static errorLog = [];
  static maxLogSize = 100;
  static listeners = new Set();

  /**
   * Handle and categorize errors
   */
  static handleError(error, context = 'unknown', options = {}) {
    const { 
      silent = false, 
      retry = false, 
      fallback = null,
      userMessage = null 
    } = options;

    const errorInfo = this.categorizeError(error, context);
    
    // Log the error
    this.logError(errorInfo);

    // Notify listeners
    this.notifyListeners(errorInfo);

    // Handle based on error type
    return this.processError(errorInfo, { silent, retry, fallback, userMessage });
  }

  /**
   * Categorize error types
   */
  static categorizeError(error, context) {
    const errorInfo = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      context,
      originalError: error,
      type: 'unknown',
      severity: 'medium',
      recoverable: false,
      userMessage: 'An unexpected error occurred',
      technicalMessage: error.message || 'Unknown error',
      code: error.code || null,
      stack: error.stack || null
    };

    // Network errors
    if (error.code === 'unavailable' || error.message?.includes('network')) {
      errorInfo.type = 'network';
      errorInfo.severity = 'high';
      errorInfo.recoverable = true;
      errorInfo.userMessage = 'Network connection issue. Please check your internet connection.';
    }
    
    // Firebase permission errors
    else if (error.code === 'permission-denied') {
      errorInfo.type = 'permission';
      errorInfo.severity = 'high';
      errorInfo.recoverable = false;
      errorInfo.userMessage = 'Access denied. Please contact support.';
    }
    
    // Firebase quota/limits
    else if (error.code === 'resource-exhausted') {
      errorInfo.type = 'quota';
      errorInfo.severity = 'high';
      errorInfo.recoverable = true;
      errorInfo.userMessage = 'Service temporarily unavailable. Please try again later.';
    }
    
    // Firebase index missing
    else if (error.code === 'failed-precondition') {
      errorInfo.type = 'configuration';
      errorInfo.severity = 'high';
      errorInfo.recoverable = false;
      errorInfo.userMessage = 'Service configuration issue. Please contact support.';
    }
    
    // Timeout errors
    else if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      errorInfo.type = 'timeout';
      errorInfo.severity = 'medium';
      errorInfo.recoverable = true;
      errorInfo.userMessage = 'Request timed out. Please try again.';
    }
    
    // Storage quota exceeded
    else if (error.name === 'QuotaExceededError') {
      errorInfo.type = 'storage';
      errorInfo.severity = 'medium';
      errorInfo.recoverable = true;
      errorInfo.userMessage = 'Storage limit reached. Some features may be limited.';
    }
    
    // Parse/data errors
    else if (error instanceof SyntaxError || error.message?.includes('JSON')) {
      errorInfo.type = 'data';
      errorInfo.severity = 'medium';
      errorInfo.recoverable = true;
      errorInfo.userMessage = 'Data format error. Please refresh the page.';
    }

    return errorInfo;
  }

  /**
   * Process error based on type and options
   */
  static processError(errorInfo, options) {
    const { silent, retry, fallback, userMessage } = options;

    // Use custom user message if provided
    if (userMessage) {
      errorInfo.userMessage = userMessage;
    }

    // Return fallback data if available
    if (fallback !== null) {
      console.warn(`Using fallback for ${errorInfo.type} error:`, errorInfo.technicalMessage);
      return {
        success: false,
        error: errorInfo,
        data: fallback,
        shouldRetry: retry && errorInfo.recoverable
      };
    }

    // Log error if not silent
    if (!silent) {
      console.error(`${errorInfo.type} error in ${errorInfo.context}:`, errorInfo.originalError);
    }

    return {
      success: false,
      error: errorInfo,
      data: null,
      shouldRetry: retry && errorInfo.recoverable
    };
  }

  /**
   * Log error to internal log
   */
  static logError(errorInfo) {
    this.errorLog.unshift(errorInfo);
    
    // Keep log size manageable
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }
  }

  /**
   * Get error statistics
   */
  static getErrorStats() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const recentErrors = this.errorLog.filter(e => now - e.timestamp.getTime() < oneHour);

    const stats = {
      total: this.errorLog.length,
      recent: recentErrors.length,
      byType: {},
      bySeverity: {},
      recoverable: 0,
      nonRecoverable: 0
    };

    this.errorLog.forEach(error => {
      // Count by type
      stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
      
      // Count recoverable vs non-recoverable
      if (error.recoverable) {
        stats.recoverable++;
      } else {
        stats.nonRecoverable++;
      }
    });

    return stats;
  }

  /**
   * Add error listener
   */
  static addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  static notifyListeners(errorInfo) {
    this.listeners.forEach(callback => {
      try {
        callback(errorInfo);
      } catch (error) {
        console.warn('Error in error listener:', error);
      }
    });
  }

  /**
   * Clear error log
   */
  static clearLog() {
    this.errorLog = [];
  }

  /**
   * Get recent errors
   */
  static getRecentErrors(limit = 10) {
    return this.errorLog.slice(0, limit);
  }
}

/**
 * Retry utility with exponential backoff
 */
export class RetryHandler {
  static async withRetry(
    operation, 
    options = {}
  ) {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      backoffFactor = 2,
      shouldRetry = (error) => true,
      onRetry = null
    } = options;

    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation(attempt);
        return { success: true, data: result, attempts: attempt + 1 };
      } catch (error) {
        lastError = error;
        
        // Don't retry on last attempt
        if (attempt === maxRetries) break;
        
        // Check if we should retry this error
        if (!shouldRetry(error)) break;
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          baseDelay * Math.pow(backoffFactor, attempt),
          maxDelay
        );
        
        // Add jitter to prevent thundering herd
        const jitteredDelay = delay + Math.random() * 1000;
        
        console.warn(`Attempt ${attempt + 1} failed, retrying in ${jitteredDelay}ms:`, error.message);
        
        if (onRetry) {
          onRetry(error, attempt + 1, jitteredDelay);
        }
        
        await new Promise(resolve => setTimeout(resolve, jitteredDelay));
      }
    }

    return { 
      success: false, 
      error: lastError, 
      attempts: maxRetries + 1 
    };
  }
}

/**
 * Circuit breaker pattern for failing services
 */
export class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000; // 1 minute
    this.monitoringPeriod = options.monitoringPeriod || 10000; // 10 seconds
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
  }

  async execute(operation) {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.resetTimeout) {
        this.state = 'HALF_OPEN';
        this.successCount = 0;
      } else {
        throw new Error('Circuit breaker is OPEN');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    
    if (this.state === 'HALF_OPEN') {
      this.successCount++;
      if (this.successCount >= 3) { // Require 3 successes to close
        this.state = 'CLOSED';
      }
    }
  }

  onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
    }
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      successCount: this.successCount
    };
  }
}

export default ErrorHandler;